{
  "common": {
    "save": "Save changes",
    "cancel": "Cancel",
    "delete": "Delete",
    "loading": "Loading…",
    "language": "Language",
    "all": "All",
    "actions": "Actions",
    "search": "Search",
    "noIssues": "✓ Nothing wrong — safe to publish"
  },
  "nav": {
    "rates": "Rates",
    "simulator": "Simulator",
    "events": "Events",
    "lot": "Lot setup",
    "orders": "Orders",
    "revenue": "Revenue & payouts",
    "promotions": "Parking credit",
    "team": "Team & log"
  },
  "shell": {
    "currentLot": "Current lot",
    "lotCount": "{{code}} · {{count}} lots",
    "online": "Lot is live",
    "offline": "Lot is not live",
    "offlineNote": "Taking a lot offline never affects parking already running",
    "owner": "Owner",
    "staff": "Staff",
    "signOut": "Sign out",
    "loadingLots": "Loading lots…",
    "menu": "Menu",
    "closeMenu": "Close the menu"
  },
  "login": {
    "title": "ParkingPay operator console",
    "subtitle": "Rates · pricing simulator · revenue and payouts",
    "email": "Email",
    "password": "Password",
    "signIn": "Sign in",
    "signingIn": "Signing in…",
    "failed": "Sign-in failed",
    "devHint": "Dev seed accounts: owner maria@loopparking.com / staff devon@loopparking.com, password ParkingPay!2026. Staff can read orders and look up plates; they cannot change rates or see payouts."
  },
  "rates": {
    "title": "Rates",
    "hint": "Enter the final price the driver pays. Saving mints a new version — parking already running keeps the version it started on.",
    "history": "Change history",
    "saveDraft": "Save draft",
    "publish": "Publish v{{version}}",
    "loadFailed": "Couldn't load rates",
    "saveFailed": "Couldn't save",
    "publishFailed": "Couldn't publish",
    "colMax": "Up to",
    "colPrice": "Final price",
    "within": "Up to {{duration}}",
    "minimumMinutes": "Minimum purchase (minutes)",
    "incrementMinutes": "Billing increment (minutes)",
    "dailyCap": "24-hour cap",
    "extraDay": "Each further day beyond 24h",
    "preflight": "Pre-publish check",
    "specials": "Special rates",
    "specialWindow": "Entry {{from}}–{{to}}",
    "specialExit": ", must exit before {{time}}",
    "specialNote": "Compared against the tier table, lowest wins; the driver is shown which rate applied",
    "panel": "When several rates match, the cheapest for the driver wins and the app names it. Event pricing overrides everything and is never compared.\nSaving mints a new version with its own effective time; parking already running keeps the version it started on.",
    "published": "Live",
    "draft": "Draft",
    "superseded": "Superseded",
    "notPublished": "Not published",
    "editedAt": "Edited {{time}}"
  },
  "simulator": {
    "title": "Pricing simulator and self-check",
    "hint": "What makes self-serve pricing safe: see what the driver would pay before deciding to publish.",
    "startAt": "Arrival (lot local time)",
    "minutes": "Minutes parked",
    "requestedMinutes": "Driver requested",
    "entitlementMinutes": "Parking time granted",
    "run": "Work out the price",
    "running": "Calculating…",
    "appliedRate": "Rate applied",
    "standardTier": "Standard tier",
    "beatsTier": "Beats the tier base of {{price}}",
    "finalPrice": "Final price",
    "selfCheck": "Rate self-check",
    "curve": "24-hour price curve",
    "curveDuration": "Duration",
    "hours": "{{count}}h",
    "minutesShort": "{{count}} min",
    "total": "Total",
    "arrivesAt": "Arrives {{hour}}:00",
    "panel": "The curve samples by arrival time, showing what the same stay costs at different hours. Early-bird, night and event pricing show up immediately as a change in bar height."
  },
  "events": {
    "title": "Event calendar and event pricing",
    "hint": "A game-day flat rate. It overrides every other rule and is never compared against them.",
    "new": "+ New event",
    "name": "Event name",
    "startsAt": "Starts",
    "endsAt": "Ends",
    "price": "Event price",
    "lots": "Lots (none selected = all)",
    "saveEvent": "Save event",
    "createFailed": "Couldn't create the event",
    "colName": "Event",
    "colDate": "Date",
    "colTime": "Time",
    "colPrice": "Price",
    "colLots": "Lots",
    "panel": "While an event is live, the driver sees its name next to the price with an \"event pricing\" marker. Event pricing is never compared against the tier table or early-bird rates — it simply overrides them."
  },
  "lotSetup": {
    "title": "Lot setup",
    "createTitle": "Create a lot",
    "hint": "Build a lot and start charging within fifteen minutes, without platform involvement. Taking it offline never affects parking already running.",
    "online": "Live",
    "offline": "Temporarily offline",
    "draft": "Draft",
    "name": "Lot name",
    "nameZh": "Chinese lot name",
    "address": "Street address",
    "city": "City",
    "state": "State",
    "postalCode": "ZIP code",
    "latitude": "Latitude",
    "longitude": "Longitude",
    "capacity": "Number of spaces",
    "maximumCapacity": "Maximum parking spaces",
    "capacityLimitActive": "New vehicles cannot pay after {{count}} vehicles are parked or checking out.",
    "capacityLimitHint": "Enter the maximum number of vehicles that can park at one time.",
    "spaceNumberStart": "First space number",
    "spaceNumberHint": "Enter a 4-digit starting number. The last number is calculated from capacity.",
    "spaceNumberRange": "Space numbers {{start}}–{{end}}",
    "invalidSpaceRange": "Use a 4-digit starting number and keep the full range at or below 9999.",
    "numberingTitle": "Parking space numbering",
    "numberingDescription": "Configure numbered spaces or enforce a maximum vehicle capacity for an unnumbered lot.",
    "numberingEnabled": "Require a space number",
    "numberingEnabledDescription": "Turn this off when this lot does not have numbered parking spaces.",
    "numberingDisabled": "Not used",
    "numberingCurrentRange": "Current range",
    "numberingEdit": "Edit numbering",
    "numberingReview": "Review change",
    "numberingCancel": "Cancel",
    "numberingProtected": "This setting is protected because changing it affects the numbers printed in the lot. Regular lot saves never change it.",
    "numberingConfirmTitle": "Confirm numbering change",
    "numberingConfirmDescription": "Review this setting carefully. Drivers will only be asked for a space number when numbering is enabled.",
    "numberingCurrent": "Current",
    "numberingNew": "New",
    "numberingSpaceCount": "{{count}} spaces",
    "numberingConfirmAction": "Confirm and update",
    "numberingSaved": "Parking space numbering updated",
    "numberingSaveFailed": "Couldn't update parking space numbering",
    "heightCm": "Height limit (cm, optional)",
    "timezone": "Time zone",
    "evChargers": "EV chargers",
    "status": "Payment status",
    "availability": "Manual availability",
    "availabilityUpdated": "Operator update · {{time}}",
    "availabilityUnknownTime": "Not updated yet",
    "availabilityStatus": {
      "unknown": "Unknown",
      "available": "Available",
      "busy": "Busy",
      "full": "Full"
    },
    "accessEn": "Entrance directions (English)",
    "accessZh": "Entrance directions (Chinese)",
    "photos": "Entrance photos",
    "uploadPhotos": "Upload photos",
    "photoFormats": "JPG, PNG or WebP · max 8 MB each",
    "photoCount": "{{count}} of {{max}} photos",
    "photoAlt": "Entrance photo {{number}}",
    "primaryPhoto": "Cover",
    "makePrimary": "Make cover photo",
    "removePhoto": "Remove photo",
    "uploading": "Uploading photo",
    "retryUpload": "Retry upload",
    "photoUploadFailed": "Couldn't upload the photo",
    "photoTypeError": "Choose a JPG, PNG or WebP image.",
    "photoSizeError": "Each photo must be 8 MB or smaller.",
    "photoLimit": "A lot can have up to {{count}} photos.",
    "newLot": "New lot",
    "cancelCreate": "Cancel",
    "create": "Create draft",
    "save": "Save changes",
    "saved": "Lot saved",
    "saveFailed": "Couldn't save the lot",
    "noLotsOwner": "No lots yet. Create the first draft to generate its lot code and QR sign.",
    "noLotsStaff": "No lots have been assigned to this organization yet.",
    "hours": "Business hours (configurable per weekday)",
    "qrTitle": "Lot QR code",
    "qrAlt": "{{lot}} QR code",
    "preview": "Preview the landing page",
    "downloadQr": "Download",
    "printQr": "Print",
    "qrAfterCreate": "The platform generates the lot code and QR after the draft is created.",
    "weekday": {
      "1": "Mon",
      "2": "Tue",
      "3": "Wed",
      "4": "Thu",
      "5": "Fri",
      "6": "Sat",
      "7": "Sun"
    }
  },
  "orders": {
    "title": "Orders and parking in progress",
    "hint": "Plate lookup is a manual spot check. V1 is not an enforcement system.",
    "exportCsv": "Export CSV",
    "inProgress": "In progress ({{count}})",
    "autoRefresh": "Refreshes every 30 seconds",
    "colPlate": "Plate",
    "colSpace": "Space",
    "colLot": "Lot",
    "colStart": "Started",
    "colExpiry": "Ends",
    "colRemaining": "Left",
    "colPaid": "Paid",
    "colRefunded": "Refunded",
    "colActions": "Actions",
    "manage": "Manage",
    "history": "Past orders",
    "from": "From",
    "to": "To",
    "plateFilter": "Plate (optional)",
    "colRef": "Order",
    "colTime": "When",
    "colDuration": "Duration",
    "withExtensions": " (+{{count}} extensions)",
    "lookupTitle": "Plate lookup",
    "lookup": "Look up",
    "lookupFailed": "Lookup failed — check the plate format",
    "paidOk": "Paid and parking",
    "paidOkDetail": "{{lot}} · ends {{time}} · {{remaining}} left",
    "notPaid": "No valid parking found",
    "panel": "V1 is a manual spot check for the operator. There is no LPR or gate feed, so every patrol note is explicitly manual.",
    "manageTitle": "Order actions",
    "close": "Close",
    "patrolNote": "Manual patrol note",
    "addNote": "Add note",
    "correctPlate": "Correct plate",
    "reasonRequired": "Reason required (at least 5 characters)",
    "saveCorrection": "Save correction",
    "refund": "Refund",
    "refundFull": "Refund full order",
    "refunded": "Already refunded",
    "refundHistory": "Refund history",
    "patrolHistory": "Patrol history",
    "none": "None",
    "actionFailed": "Action failed"
  },
  "revenue": {
    "title": "Revenue and payouts · {{month}}",
    "hint": "Track final customer charges, refunds, platform fees and Stripe payouts.",
    "prevMonth": "Previous",
    "nextMonth": "Next",
    "noAccess": "Couldn't load revenue (staff accounts cannot see this).",
    "transactions": "Transactions",
    "transactionsSub": "first payments and extensions",
    "grossTotal": "Gross revenue",
    "grossTotalSub": "final customer charges",
    "refunds": "Refunds",
    "refundsSub": "accepted this period",
    "platformFee": "Platform fee",
    "platformFeeSub": "charged on final price",
    "net": "Net to you",
    "netSub": "after refunds and fees",
    "daily": "Daily gross",
    "noTransactions": "No transactions this month",
    "payouts": "Payouts",
    "weekly": "Weekly settlement",
    "paid": "Paid",
    "pending": "Pending",
    "noPayouts": "No payouts yet. The settlement period and the paying entity are decided by your Stripe Connect configuration.",
    "panel": "Payout timing and the paying entity follow the connected Stripe account configuration. Use this view to reconcile customer charges with deposits."
  },
  "team": {
    "title": "Accounts, roles and the audit log",
    "hint": "Control what each staff member can do and which parking lots they can do it in. Changes take effect immediately.",
    "invite": "Invite staff",
    "name": "Name",
    "email": "Email",
    "owner": "Owner",
    "staff": "Staff",
    "members": "Active members",
    "pendingInvitations": "Pending invitations",
    "noPendingInvitations": "There are no pending invitations.",
    "pending": "Pending",
    "permissions": "Staff permissions",
    "lotScope": "Parking lot access",
    "lotCount": "{{selected}} of {{total}} selected",
    "selectAllLots": "Select all",
    "clearLots": "Clear",
    "allLots": "All current and future parking lots",
    "noLots": "Create a parking lot before inviting staff.",
    "noLotsAssigned": "No parking lots assigned",
    "noPermissions": "No permissions assigned",
    "ownerScope": "All permissions, including team management",
    "editPermissions": "Edit access",
    "remove": "Remove staff member",
    "confirmRemove": "Confirm removal",
    "removeFailed": "Couldn't remove the staff member",
    "permissionUpdateFailed": "Couldn't update permissions",
    "sendInvite": "Send invite",
    "sending": "Sending…",
    "inviteFailed": "Couldn't send the invite",
    "invitationSent": "Invitation sent to {{email}}",
    "resend": "Resend",
    "resent": "The invitation was sent again.",
    "resendFailed": "Couldn't resend the invitation",
    "revoke": "Revoke invitation",
    "confirmRevoke": "Confirm revoke",
    "revokeFailed": "Couldn't revoke the invitation",
    "expiresAt": "Expires {{date}}",
    "nameRequired": "Enter the staff member's name.",
    "emailInvalid": "Enter a valid email address.",
    "permissionRequired": "Select at least one permission.",
    "lotRequired": "Select at least one parking lot.",
    "ownerOnly": "Only an owner can manage team members and permissions.",
    "auditLog": "Audit log",
    "auditEmpty": "Nothing logged yet.",
    "permissionLabels": {
      "lots_manage": "Lot settings",
      "rates_manage": "Rates and pricing",
      "events_manage": "Event pricing",
      "orders_manage": "Orders and patrol",
      "refunds_manage": "Order refunds",
      "revenue_view": "Revenue and payouts",
      "promotions_manage": "Parking credit"
    },
    "permissionDescriptions": {
      "lots_manage": "Edit lot details, hours and entrance photos",
      "rates_manage": "View, simulate, edit and publish parking rates",
      "events_manage": "Create and delete special event pricing",
      "orders_manage": "View orders, look up plates, correct plates and add patrol notes",
      "refunds_manage": "View orders and issue full refunds",
      "revenue_view": "View revenue and Stripe payouts",
      "promotions_manage": "Create, change and revoke parking-credit campaigns"
    }
  },
  "acceptInvite": {
    "title": "Accept your staff invitation",
    "subtitle": "{{name}}, {{organization}} invited you to its parking operations console. Set your own password to continue.",
    "password": "Create password",
    "passwordHint": "At least 10 characters",
    "confirmPassword": "Confirm password",
    "accept": "Accept invitation and sign in",
    "accepting": "Creating account…",
    "passwordTooShort": "Your password must be at least 10 characters.",
    "passwordMismatch": "The passwords do not match.",
    "failed": "Couldn't accept the invitation",
    "invalidTitle": "This invitation is not valid",
    "invalidBody": "It may have already been accepted, revoked or expired. Ask the owner who invited you to resend it."
  },
  "promotions": {
    "title": "Parking credit",
    "hint": "A first-time device is given a balance that pays for real parking and is spent down across visits. The total budget is the only hard limit — device identifiers can be reset, so cap what a campaign can ever hand out.",
    "new": "+ New campaign",
    "newTitle": "New parking-credit campaign",
    "editTitle": "Edit parking-credit campaign",
    "name": "Campaign name",
    "grant": "Per device",
    "budget": "Total budget",
    "ttl": "Credit expires after (days)",
    "ttlNever": "leave empty for never",
    "startsAt": "Starts",
    "endsAt": "Ends",
    "localTime": "Entered in this device's time zone",
    "create": "Create campaign",
    "save": "Save campaign",
    "edit": "Edit",
    "createFailed": "Couldn't create the campaign",
    "updateFailed": "Couldn't save the change",
    "invalidAmounts": "Enter valid amounts and keep the total budget at or above the per-device amount.",
    "invalidTtl": "Expiry must be a whole number of days greater than zero, or blank for no expiry.",
    "invalidWindow": "The end time must be after the start time.",
    "status": {
      "draft": "Draft",
      "active": "Active",
      "paused": "Paused",
      "ended": "Ended"
    },
    "activate": "Turn on",
    "pause": "Pause",
    "end": "End campaign",
    "colName": "Campaign",
    "colStatus": "Status",
    "colGrant": "Per device",
    "colBudget": "Budget",
    "colGranted": "Handed out",
    "colRedeemed": "Actually spent",
    "colDevices": "Devices",
    "empty": "No campaigns yet.",
    "grantsTitle": "Grant records",
    "grantsHint": "Search by the device id the app shows. It is hashed before it reaches the database, and the lookup hashes your query the same way.",
    "deviceSearch": "Device id",
    "clear": "Clear",
    "colDevice": "Device (hashed)",
    "colUser": "Account",
    "colAmount": "Granted",
    "colBalance": "Balance left",
    "colPlatform": "Platform",
    "colGrantedAt": "Granted",
    "colExpiry": "Expires",
    "revoke": "Revoke",
    "revokeConfirm": "Revoke this grant? Only the unspent balance is taken back — parking that already happened is not undone.",
    "revoked": "Revoked",
    "granted": "Granted",
    "grantsEmpty": "No grants match.",
    "never": "Never",
    "panel": "Credit is a payment method, not a discount. It is applied to the final parking price. Credit that is never spent costs nothing — \"actually spent\" is the number that reflects real cost."
  },
  "errors": {
    "invalid_credentials": "Email or password is incorrect.",
    "no_organization": "This account isn't part of any parking operator yet.",
    "lot_not_found": "That lot no longer exists.",
    "lot_not_owned": "The selection includes a lot that belongs to another operator.",
    "short_code_exhausted": "Couldn't allocate a lot code. Try again.",
    "space_number_start_required": "Set a 4-digit starting number before enabling space numbering.",
    "invalid_space_range": "Use a 4-digit starting number and keep the full range at or below 9999.",
    "invalid_event_window": "The event must end after it starts.",
    "event_not_found": "That event no longer exists.",
    "rate_version_not_found": "That rate version no longer exists.",
    "rate_version_invalid": "The rates have errors and can't be published.",
    "invalid_plate": "Plates can only contain letters and digits, 2–10 characters.",
    "order_not_found": "That order no longer exists.",
    "nothing_to_refund": "This order has no payment that can be refunded.",
    "refund_provider_missing": "That payment has no provider transaction id, so it can't be refunded automatically.",
    "promotion_name_required": "The campaign needs a name.",
    "grant_must_be_positive": "The per-device amount must be more than zero.",
    "budget_below_grant": "The total budget cannot be smaller than the per-device amount.",
    "budget_below_granted": "The budget cannot go below what has already been handed out.",
    "promotion_not_found": "That campaign no longer exists.",
    "promotion_invalid_window": "The campaign end time must be after its start time.",
    "promotion_active_exists": "Another parking-credit campaign is active. Pause or end it first.",
    "grant_not_found": "That grant record no longer exists.",
    "forbidden": "Your account doesn't have permission for that.",
    "permission_required": "Your account doesn't have permission for that.",
    "lot_assignment_required": "Select at least one parking lot.",
    "lot_assignment_invalid": "One or more selected parking lots are no longer available.",
    "team_member_exists": "That email is already a team member.",
    "operator_email_in_use": "That email is already used by another admin account.",
    "invitation_email_failed": "The invitation email could not be sent. Try again shortly.",
    "invitation_not_found": "That invitation no longer exists.",
    "invitation_invalid": "That invitation link is invalid or expired.",
    "team_member_not_found": "That staff member no longer exists.",
    "cannot_remove_self": "You cannot remove your own account.",
    "unauthorized": "Please sign in again.",
    "rate_limited": "Too many attempts. Wait a moment and try again.",
    "internal_error": "The service is temporarily unavailable. Try again shortly.",
    "network_error": "No connection. Check your network and try again.",
    "photo_empty": "Choose a non-empty image.",
    "photo_too_large": "Each photo must be 8 MB or smaller.",
    "photo_type_unsupported": "Choose a JPG, PNG or WebP image.",
    "photo_invalid": "That file isn't a valid image.",
    "photo_storage_unavailable": "Photo storage is temporarily unavailable.",
    "unknown_error": "Something went wrong. Please try again."
  },
  "issues": {
    "bad_minimum": "The minimum purchase must be a positive whole number of minutes",
    "bad_increment": "The billing increment must be a positive whole number of minutes",
    "no_tiers": "At least one duration tier is required",
    "bad_tier": "Tier duration caps must be positive",
    "bad_price": "Tier prices cannot be negative",
    "duplicate_tier": "Two tiers share the same duration cap",
    "cheaper_when_longer": "A longer tier is cheaper than a shorter one — drivers will see it marked \"better value\"",
    "ladder_gap": "The tiers stop short of 24 hours, so longer stays fall straight to the daily cap",
    "cap_below_ladder": "The 24-hour cap is below the longest tier, so a long stay would cost less than a short one",
    "bad_window": "A special rate's entry window must end after it starts",
    "window_before_open": "A special rate's entry window opens before business hours — fix before publishing",
    "exit_after_close": "A special rate's exit deadline is after closing time"
  },
  "units": {
    "fullDay": "24 hours",
    "minutes": "{{count}} min",
    "hours": "{{count}}h",
    "hoursMinutes": "{{hours}}h {{minutes}}m"
  }
}
